import React, { useMemo, useState } from 'react';
import { Resizable, ResizeCallbackData } from 'react-resizable';

import * as Styled from './resizable-container.style';

export type ResizableContainerProps = {
  initialWidth?: number;
  minSize?: number;
  maxSize?: number;
  resizeHandles?: 'w' | 'e';
  open?: boolean;
  smoothTransition?: boolean;
  onWidthChange?: (width: number) => void;
  children: React.ReactNode;
};

export const ResizableContainer: React.FC<ResizableContainerProps> = ({
  initialWidth = Styled.initialContainerWidth,
  minSize = Styled.minContainerWidth,
  maxSize = Styled.maxContainerWidth,
  resizeHandles = 'w',
  open = true,
  smoothTransition = false,
  onWidthChange,
  children,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [width, setWidth] = useState(initialWidth);

  const handleResizeStart = () => setIsResizing(true);

  const handleResizeMove = (
    _: React.SyntheticEvent,
    { size }: ResizeCallbackData,
  ) => setWidth(size.width);

  const handleResizeEnd = () => {
    setIsResizing(false);
    onWidthChange?.(width);
  };

  const minConstraints = useMemo<[number, number]>(
    () => [minSize, 0],
    [minSize],
  );
  const maxConstraints = useMemo<[number, number]>(
    () => [maxSize, 0],
    [maxSize],
  );

  return (
    <Resizable
      width={width}
      axis="x"
      onResizeStart={handleResizeStart}
      onResize={handleResizeMove}
      onResizeStop={handleResizeEnd}
      resizeHandles={[resizeHandles]}
      handle={(_, ref) => (
        <Styled.ResizeHandle
          $isResizing={isResizing}
          $position={resizeHandles}
          ref={ref}
        />
      )}
      minConstraints={minConstraints}
      maxConstraints={maxConstraints}
    >
      <Styled.Content
        style={{ width: `${open ? width : 0}px` }}
        $smoothTransition={smoothTransition}
        $isResizing={isResizing}
      >
        {children}
      </Styled.Content>
    </Resizable>
  );
};
